import { useEffect, useRef } from 'react'
import { useToastStore, type Toast } from '@/stores/toast'
import { X, CheckCircle2, AlertTriangle, AlertCircle, Info } from 'lucide-react'
import { cn } from '@/utils/cn'

const variantConfig = {
  error: {
    bg: 'bg-error-light',
    border: 'border-error/20',
    text: 'text-on-error-soft',
    icon: AlertCircle,
  },
  success: {
    bg: 'bg-success-light',
    border: 'border-success/20',
    text: 'text-on-success-soft',
    icon: CheckCircle2,
  },
  warning: {
    bg: 'bg-warning-light',
    border: 'border-warning/20',
    text: 'text-on-warning-soft',
    icon: AlertTriangle,
  },
  info: {
    bg: 'bg-info-light',
    border: 'border-info/20',
    text: 'text-on-info-soft',
    icon: Info,
  },
}

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  const ref = useRef<HTMLDivElement>(null)
  const config = variantConfig[toast.type]
  const Icon = config.icon

  // Animate in on mount
  useEffect(() => {
    const el = ref.current
    if (!el) return
    el.style.opacity = '0'
    el.style.transform = 'translateY(8px) scale(0.97)'
    requestAnimationFrame(() => {
      el.style.transition = 'opacity 200ms var(--ease-out-expo), transform 200ms var(--ease-out-expo)'
      el.style.opacity = '1'
      el.style.transform = 'translateY(0) scale(1)'
    })
  }, [])

  return (
    <div
      ref={ref}
      className={cn(
        'px-4 py-3 rounded-lg shadow-md border text-sm flex items-start gap-3',
        config.bg,
        config.border,
      )}
    >
      <Icon className={cn('w-4 h-4 mt-0.5 shrink-0', config.text)} />
      <div className="flex-1 min-w-0">
        <p className="font-medium text-ink">{toast.title}</p>
        {toast.description && (
          <p className="text-ink-secondary text-[13px] mt-0.5">{toast.description}</p>
        )}
        {toast.action && (
          <button
            type="button"
            onClick={() => { toast.action?.onClick(); onDismiss() }}
            className="mt-1.5 text-xs font-medium text-claro-600 hover:text-claro-800 transition-colors"
          >
            {toast.action.label}
          </button>
        )}
      </div>
      <button
        onClick={onDismiss}
        className="shrink-0 p-0.5 rounded hover:bg-ink/5 transition-colors"
        aria-label="Dismiss notification"
      >
        <X className="w-3.5 h-3.5 text-ink-tertiary" />
      </button>
    </div>
  )
}

export function ToastContainer() {
  const { toasts, removeToast } = useToastStore()

  // Always render the live region so screen readers detect additions
  return (
    <div
      className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2 w-[360px] max-w-[calc(100vw-2rem)]"
      role="status"
      aria-live="polite"
      aria-relevant="additions"
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={() => removeToast(t.id)} />
      ))}
    </div>
  )
}
